import React, { useRef, useEffect, useState } from 'react';
import { X, Home, Zap, BookOpen, Table, MessageSquare, Loader2, Settings, FileCode, Dna, FolderOpen } from 'lucide-react';
import { useTabManager } from '../../contexts/TabContext';
import { Tab, TabType } from '../../types/tabs';
import { clsx } from 'clsx';

// Icon mapping for tab types
const tabIcons: Record<TabType, React.ComponentType<any>> = {
  home: Home,
  generation: Zap,
  entry: BookOpen,
  table: Table,
  chat: MessageSquare,
  settings: Settings,
  molecule: FileCode,
  odyssey: Dna,
  filepreparer: FolderOpen
};

interface TabBarProps {
  onNewEntry?: () => void;
  onNewTable?: () => void;
  onTabSwitch?: (tabId: string) => void; // Custom tab switch handler
  onCloseTab?: (tabId: string) => Promise<void>; // Custom close handler
}

export function TabBar({ onNewEntry, onNewTable, onTabSwitch, onCloseTab }: TabBarProps) {
  const { tabs, activeTabId, switchTab, closeTab, reorderTabs } = useTabManager();
  const tabsContainerRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const [draggedTab, setDraggedTab] = useState<string | null>(null);
  const [previewTabs, setPreviewTabs] = useState<Tab[] | null>(null);
  const [localClosingTabs, setLocalClosingTabs] = useState<Set<string>>(new Set());

  // Check scroll availability
  const checkScroll = () => {
    const container = tabsContainerRef.current;
    if (container) {
      setCanScrollLeft(container.scrollLeft > 0);
      setCanScrollRight(
        container.scrollLeft < container.scrollWidth - container.clientWidth
      );
    }
  };

  useEffect(() => {
    checkScroll();
    window.addEventListener('resize', checkScroll);
    return () => window.removeEventListener('resize', checkScroll);
  }, [tabs]);

  const handleScroll = (direction: 'left' | 'right') => {
    const container = tabsContainerRef.current;
    if (container) {
      const scrollAmount = 200;
      container.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth'
      });
    }
  };

  const handleTabClick = (tabId: string, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    // Don't switch to tab if it's currently closing
    if (localClosingTabs.has(tabId)) {
      return;
    }
    
    // Use custom tab switch handler if provided, otherwise use default
    if (onTabSwitch) {
      onTabSwitch(tabId);
    } else {
      switchTab(tabId);
    }
  };

  const handleCloseTab = async (tabId: string, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    // Find the tab to check if it's closable
    const tab = tabs.find(t => t.id === tabId);
    if (!tab) {
      console.warn(`Tab ${tabId} not found`);
      return;
    }
    
    // Check if tab is closable (home tabs are not closable by default)
    if (tab.type === 'home' && !tab.closable) {
      console.warn(`Tab ${tabId} is not closable (home tab)`);
      return;
    }
    
    // Check if tab is already being closed
    if (localClosingTabs.has(tabId)) {
      console.warn(`Tab ${tabId} is already being closed`);
      return;
    }
    
    // Mark tab as closing
    setLocalClosingTabs(prev => new Set([...prev, tabId]));
    
    try {
      // Use custom close handler if provided, otherwise use default
      if (onCloseTab) {
        await onCloseTab(tabId);
      } else {
        await closeTab(tabId);
      }
      console.log('✅ Tab closed successfully:', tabId);
    } catch (error) {
      console.error('❌ Error closing tab:', error);
    } finally {
      // Remove from closing set after a short delay
      setTimeout(() => {
        setLocalClosingTabs(prev => {
          const newSet = new Set(prev);
          newSet.delete(tabId);
          return newSet;
        });
      }, 300);
    }
  };

  const handleDragStart = (e: React.DragEvent, tabId: string) => {
    setDraggedTab(tabId);
    setPreviewTabs([...tabs]); // Initialize preview with current tab order
    e.dataTransfer.effectAllowed = 'move';
    
    // Create an invisible drag image to hide the default ghost image
    const dragImage = new Image();
    dragImage.src = 'data:image/gif;base64,R0lGODlhAQABAIAAAAUEBAAAACwAAAAAAQABAAACAkQBADs=';
    e.dataTransfer.setDragImage(dragImage, 0, 0);
  };

  const handleDragOver = (e: React.DragEvent, tabId: string) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    
    if (draggedTab && draggedTab !== tabId && previewTabs) {
      const draggedIndex = previewTabs.findIndex(tab => tab.id === draggedTab);
      const targetIndex = previewTabs.findIndex(tab => tab.id === tabId);
      
      if (draggedIndex !== -1 && targetIndex !== -1) {
        const newPreviewTabs = [...previewTabs];
        const [removed] = newPreviewTabs.splice(draggedIndex, 1);
        newPreviewTabs.splice(targetIndex, 0, removed);
        setPreviewTabs(newPreviewTabs);
      }
    }
  };

  const handleDragLeave = (e: React.DragEvent) => {
    // No longer needed since we're using preview tabs
  };

  const handleDrop = (e: React.DragEvent, targetTabId: string) => {
    e.preventDefault();
    
    if (previewTabs && reorderTabs) {
      reorderTabs(previewTabs); // Commit the preview order
    }
    
    setDraggedTab(null);
    setPreviewTabs(null);
  };

  const handleDragEnd = () => {
    setDraggedTab(null);
    setPreviewTabs(null); // Clear preview if drag is cancelled
  };

  return (
    <div className="bg-gray-100 relative w-full" style={{ maxWidth: '100vw', overflow: 'hidden' }}>
      <div className="relative flex items-center w-full">
        {/* Scroll left button */}
        {canScrollLeft && (
          <button
            onClick={() => handleScroll('left')}
            className="absolute left-0 z-20 p-1.5 bg-gray-100 hover:bg-gray-200 text-gray-600 flex-shrink-0"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
        )}

        {/* Tabs container */}
        <div
          ref={tabsContainerRef}
          className="overflow-x-auto overflow-y-hidden scrollbar-hide flex items-end"
          onScroll={checkScroll}
          style={{ 
            scrollbarWidth: 'none', 
            msOverflowStyle: 'none',
            width: '100%',
            paddingLeft: canScrollLeft ? '40px' : '0',
            paddingRight: canScrollRight ? '40px' : '8px'
          }}
        >
          {(previewTabs || tabs).map((tab, index) => {
            const Icon = tabIcons[tab.type];
            const isActive = tab.id === activeTabId;
            const isClosing = localClosingTabs.has(tab.id);
            
            return (
              <React.Fragment key={tab.id}>
                <div
                  className={clsx(
                    'group relative inline-flex items-center gap-2 px-3 py-2.5 min-w-[120px] max-w-[240px] cursor-pointer transition-all duration-200 flex-shrink-0 whitespace-nowrap',
                    {
                      'bg-white': isActive,
                      'bg-gray-50 hover:bg-gray-100': !isActive,
                      'opacity-75': draggedTab === tab.id,
                      'opacity-50 pointer-events-none': isClosing
                    }
                  )}
                  onClick={(e) => handleTabClick(tab.id, e)}
                  draggable={!isClosing}
                  onDragStart={(e) => handleDragStart(e, tab.id)}
                  onDragOver={(e) => handleDragOver(e, tab.id)}
                  onDragLeave={handleDragLeave}
                  onDrop={(e) => handleDrop(e, tab.id)}
                  onDragEnd={handleDragEnd}
                >
                  {tab.loading || isClosing ? (
                    <Loader2 className={clsx(
                      'h-4 w-4 flex-shrink-0 animate-spin',
                      isActive ? 'text-gray-700' : 'text-gray-500'
                    )} />
                  ) : (
                    <Icon className={clsx(
                      'h-4 w-4 flex-shrink-0',
                      isActive ? 'text-gray-700' : 'text-gray-500',
                      tab.error && 'text-gray-600'
                    )} />
                  )}
                  <span
                    className={clsx(
                      'text-base truncate flex-1',
                      isActive ? 'text-gray-900' : 'text-gray-600',
                      tab.error && 'text-gray-700'
                    )}
                    title={tab.error || tab.title}
                  >
                    {isClosing ? 'Closing...' : tab.title}
                  </span>
                  
                  {/* Close button - show for closable tabs */}
                  {tab.closable && !isClosing && (
                    <button
                      onClick={(e) => handleCloseTab(tab.id, e)}
                      className="ml-1 p-0.5 rounded-sm hover:bg-gray-200 transition-all flex-shrink-0 relative z-10"
                      aria-label={`Close ${tab.title}`}
                    >
                      <X className="h-4 w-4 text-gray-500 hover:text-gray-700" />
                    </button>
                  )}
                </div>
                
                {/* Separator line between tabs */}
                {index < tabs.length - 1 && (
                  <div className="w-px h-5 bg-gray-200 self-center" />
                )}
              </React.Fragment>
            );
          })}
        </div>

        {/* Scroll right button */}
        {canScrollRight && (
          <button
            onClick={() => handleScroll('right')}
            className="absolute right-0 z-20 p-1.5 bg-gray-100 hover:bg-gray-200 text-gray-600 flex-shrink-0"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        )}
      </div>
    </div>
  );
}

// CSS to hide scrollbar but keep functionality
const scrollbarHideStyle = `
  .scrollbar-hide {
    -ms-overflow-style: none;
    scrollbar-width: none;
  }
  .scrollbar-hide::-webkit-scrollbar {
    display: none;
  }
`;

// Add the style to the document
if (typeof document !== 'undefined') {
  const style = document.createElement('style');
  style.textContent = scrollbarHideStyle;
  document.head.appendChild(style);
}